import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    FiLogOut, FiMenu, FiX,
    FiShield, FiBookOpen, FiMail, FiAlertCircle, FiCheckCircle,
} from 'react-icons/fi';
import {
    FaHome, FaIdBadge, FaShoppingBag, FaTruck, FaReceipt,
} from 'react-icons/fa';
import { GooglePlayButton, AppleStoreButton } from '../StoreButtons';
import { logoutUEN, getUENUser, UEN_USER_KEY } from '../../utils';
import { resendEmailVerificationApi } from '../../Api';
import { notify } from '../Notify';
import useCooldown, { formatarCooldown } from '../../hooks/useCooldown';
import uenlogo from '../../assets/uenlogo.png';
import styles from './SidebarLayout.module.css';

// Banner amarelo quando o usuário ainda não confirmou o email.
// Some quando email_verificado_at deixar de ser null no localStorage.
function EmailVerificationBanner({ user, onResent }) {
    const [sending, setSending] = useState(false);
    // Cooldown de 10min entre reenvios. Persiste em localStorage; se o
    // backend devolver 429 com try_at, sincroniza com esse timestamp.
    const cooldown = useCooldown('uen_verify_resend_cooldown', 10);
    if (!user || user.email_verificado_at) return null;

    async function handleResend() {
        if (sending || cooldown.isActive) return;
        setSending(true);
        try {
            const res = await resendEmailVerificationApi();
            if (res.data?.alreadyVerified) {
                notify('Seu email já está confirmado!', 'success');
                onResent?.(true);
            } else {
                notify('Enviamos um novo link de confirmação para seu email.', 'success');
                cooldown.start();
            }
        } catch (err) {
            // 429 = cooldown ativo (vem com try_at do servidor — usamos pra
            // sincronizar o front com o relógio do backend).
            if (err.response?.status === 429 && err.response?.data?.try_at) {
                cooldown.startAt(err.response.data.try_at);
                notify('Aguarde antes de reenviar o link.', 'warning');
            } else {
                notify(err.response?.data?.error || 'Não foi possível reenviar o link.', 'error');
            }
        } finally {
            setSending(false);
        }
    }

    const desabilitado = sending || cooldown.isActive;
    const label = sending
        ? 'Enviando…'
        : cooldown.isActive
            ? `Aguarde ${formatarCooldown(cooldown.secondsLeft)}`
            : 'Reenviar link';

    return (
        <div style={bannerStyles.box}>
            <div style={bannerStyles.iconWrap}>
                <FiAlertCircle size={20} />
            </div>
            <div style={bannerStyles.text}>
                <strong>Confirme seu email</strong> para receber atualizações
                importantes sobre sua carteirinha. Verifique sua caixa de entrada.
            </div>
            <button
                type="button"
                onClick={handleResend}
                disabled={desabilitado}
                style={{ ...bannerStyles.btn, opacity: desabilitado ? 0.65 : 1, cursor: desabilitado ? 'not-allowed' : 'pointer' }}
            >
                <FiMail size={14} /> {label}
            </button>
        </div>
    );
}

const bannerStyles = {
    box: {
        display: 'flex', alignItems: 'center', gap: 12,
        background: '#FFFBEB', borderLeft: '4px solid #F59E0B',
        color: '#92400E', padding: '12px 16px', borderRadius: 10,
        marginBottom: 20, fontSize: '0.88rem', lineHeight: 1.5,
        flexWrap: 'wrap',
    },
    iconWrap: {
        display: 'flex', alignItems: 'center', color: '#B45309', flexShrink: 0,
    },
    text: { flex: 1, minWidth: 220 },
    btn: {
        display: 'inline-flex', alignItems: 'center', gap: 6,
        background: '#FFFFFF', color: '#B45309',
        border: '1.5px solid #F59E0B', borderRadius: 8,
        padding: '8px 14px', fontSize: '0.82rem', fontWeight: 600,
        cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0,
    },
};

// Mesmos ícones (Font Awesome) e estilo do sidebar do gerenciador:
// FaIdBadge p/ carteira, FaShoppingBag p/ produtos, FaTruck p/ logística.
// Modal mostrado logo após o login quando o email ainda NÃO foi confirmado.
// Explica por que confirmar (receber aprovação/reprovação da carteirinha) e
// deixa reenviar o link (mesmo cooldown de 10min do banner).
function EmailVerificationModal({ onClose, onResent }) {
    const [sending, setSending] = useState(false);
    const cooldown = useCooldown('uen_verify_resend_cooldown', 10);

    async function handleResend() {
        if (sending || cooldown.isActive) return;
        setSending(true);
        try {
            const res = await resendEmailVerificationApi();
            if (res.data?.alreadyVerified) {
                notify('Seu email já está confirmado!', 'success');
                onResent?.(true);
                onClose?.();
            } else {
                notify('Enviamos um novo link de confirmação para seu email.', 'success');
                cooldown.start();
            }
        } catch (err) {
            if (err.response?.status === 429 && err.response?.data?.try_at) {
                cooldown.startAt(err.response.data.try_at);
                notify('Aguarde antes de reenviar o link.', 'warning');
            } else {
                notify(err.response?.data?.error || 'Não foi possível reenviar o link.', 'error');
            }
        } finally {
            setSending(false);
        }
    }

    const desabilitado = sending || cooldown.isActive;
    const labelEnviar = sending
        ? 'Enviando…'
        : cooldown.isActive
            ? `Aguarde ${formatarCooldown(cooldown.secondsLeft)}`
            : 'Reenviar email de confirmação';

    return (
        <div style={modalStyles.overlay} role="dialog" aria-modal="true">
            <div style={modalStyles.card}>
                <button type="button" onClick={onClose} style={modalStyles.close} aria-label="Fechar">
                    <FiX size={18} />
                </button>

                <div style={modalStyles.iconWrap}><FiMail size={26} /></div>
                <h3 style={modalStyles.title}>Confirme seu email</h3>
                <p style={modalStyles.text}>
                    Recomendamos confirmar seu email para receber as atualizações
                    importantes da sua carteirinha. Sem isso, você pode perder avisos como:
                </p>

                <ul style={modalStyles.list}>
                    <li style={modalStyles.item}>
                        <FiCheckCircle size={16} style={{ color: '#059669', flexShrink: 0 }} />
                        Quando sua carteirinha for <strong>aprovada</strong>
                    </li>
                    <li style={modalStyles.item}>
                        <FiCheckCircle size={16} style={{ color: '#059669', flexShrink: 0 }} />
                        Se for <strong>reprovada</strong> — com o motivo, pra você corrigir
                    </li>
                    <li style={modalStyles.item}>
                        <FiCheckCircle size={16} style={{ color: '#059669', flexShrink: 0 }} />
                        Atualizações de <strong>pagamento e envio</strong>
                    </li>
                </ul>

                <button
                    type="button"
                    onClick={handleResend}
                    disabled={desabilitado}
                    style={{ ...modalStyles.btnPrimary, opacity: desabilitado ? 0.65 : 1, cursor: desabilitado ? 'not-allowed' : 'pointer' }}
                >
                    <FiMail size={16} /> {labelEnviar}
                </button>
                <button type="button" onClick={onClose} style={modalStyles.btnSecondary}>
                    Agora não
                </button>
            </div>
        </div>
    );
}

const modalStyles = {
    overlay: {
        position: 'fixed', inset: 0, zIndex: 10000,
        background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(2px)',
        display: 'grid', placeItems: 'center', padding: 20,
    },
    card: {
        position: 'relative', width: '100%', maxWidth: 440, background: '#FFFFFF',
        borderRadius: 16, padding: '1.75rem 1.6rem', textAlign: 'center',
        boxShadow: '0 24px 48px -12px rgba(15,23,42,0.3)',
    },
    close: {
        position: 'absolute', top: 12, right: 12, width: 32, height: 32,
        borderRadius: 8, border: 'none', background: '#F1F5F9', color: '#64748B',
        cursor: 'pointer', display: 'grid', placeItems: 'center',
    },
    iconWrap: {
        width: 56, height: 56, borderRadius: 16, margin: '0 auto 0.9rem',
        background: 'linear-gradient(135deg, #059669, #10B981)', color: '#FFFFFF',
        display: 'grid', placeItems: 'center',
    },
    title: { fontSize: '1.25rem', fontWeight: 800, color: '#0F172A', margin: '0 0 0.5rem' },
    text: { fontSize: '0.9rem', color: '#475569', lineHeight: 1.55, margin: '0 0 1rem' },
    list: {
        listStyle: 'none', padding: 0, margin: '0 0 1.4rem', textAlign: 'left',
        display: 'flex', flexDirection: 'column', gap: 10,
    },
    item: {
        display: 'flex', alignItems: 'center', gap: 8,
        fontSize: '0.88rem', color: '#334155', lineHeight: 1.4,
    },
    btnPrimary: {
        width: '100%', padding: '0.8rem', borderRadius: 10, border: 'none',
        background: 'linear-gradient(135deg, #059669, #10B981)', color: '#FFFFFF',
        fontWeight: 700, fontSize: '0.9rem', fontFamily: 'inherit',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    },
    btnSecondary: {
        width: '100%', marginTop: 10, padding: '0.7rem', borderRadius: 10,
        border: 'none', background: 'transparent', color: '#64748B',
        fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer', fontFamily: 'inherit',
    },
};

const menuItems = [
    { label: 'Início',       icon: FaHome,         path: '/dashboard' },
    { label: 'Carteirinha',  icon: FaIdBadge,      path: '/minhacarteirinha' },
    { label: 'Produtos',     icon: FaShoppingBag,  path: '/produtos' },
    { label: 'Rastreamento', icon: FaTruck,        path: '/rastreamento' },
    { label: 'Histórico',    icon: FaReceipt,      path: '/meuhistorico' },
];

function getInitials(name) {
    if (!name) return 'U';
    return name
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map(p => p[0])
        .join('')
        .toUpperCase();
}

export default function SidebarLayout({ children }) {
    const [isOpen, setIsOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const [user, setUser] = useState(() => getUENUser());
    const [showEmailModal, setShowEmailModal] = useState(false);

    // Modal de confirmação de email — abre UMA vez logo após o login (a flag é
    // setada no LoginUser) e só se o email ainda não foi confirmado. Em
    // navegações seguintes não reaparece, porque a flag já foi consumida.
    useEffect(() => {
        if (sessionStorage.getItem('uen_email_prompt_pending')) {
            sessionStorage.removeItem('uen_email_prompt_pending');
            const u = getUENUser();
            if (u && !u.email_verificado_at) setShowEmailModal(true);
        }
    }, []);

    // Fecha drawer ao mudar de rota + re-lê user do localStorage
    // (pra o banner sumir se o email foi confirmado em outra aba).
    useEffect(() => {
        setIsOpen(false);
        setUser(getUENUser());
    }, [location.pathname]);

    // Quando o resend descobre que já tava confirmado, sincroniza o
    // localStorage pra o banner sumir sem precisar F5.
    function marcarEmailConfirmadoLocal() {
        const atual = getUENUser();
        if (!atual) return;
        const atualizado = { ...atual, email_verificado_at: new Date().toISOString() };
        localStorage.setItem(UEN_USER_KEY, JSON.stringify(atualizado));
        setUser(atualizado);
    }

    const handleLogout = () => {
        logoutUEN();
        navigate('/login');
    };

    const userName = user?.nome_completo || user?.nome || user?.name || 'Estudante';
    const userEmail = user?.email || '';

    return (
        <div className={styles.layout}>
            {showEmailModal && (
                <EmailVerificationModal
                    onClose={() => setShowEmailModal(false)}
                    onResent={(alreadyVerified) => {
                        if (alreadyVerified) marcarEmailConfirmadoLocal();
                    }}
                />
            )}

            {/* Mobile topbar */}
            <div className={styles.mobileTopbar}>
                <div className={styles.mobileBrand}>
                    <img src={uenlogo} alt="UEN" className={styles.mobileBrandImg} />
                </div>
                <button
                    className={styles.hamburger}
                    onClick={() => setIsOpen(true)}
                    aria-label="Abrir menu"
                >
                    <FiMenu size={20} />
                </button>
            </div>

            {/* Overlay mobile */}
            <div
                className={`${styles.overlay} ${isOpen ? styles.overlayOpen : ''}`}
                onClick={() => setIsOpen(false)}
            />

            {/* Sidebar */}
            <aside className={`${styles.sidebar} ${isOpen ? styles.sidebarOpen : ''}`}>
                <div className={styles.sidebarHeader}>
                    <div className={styles.brand}>
                        <img src={uenlogo} alt="UEN" className={styles.brandImg} />
                        <button
                            className={styles.hamburger}
                            onClick={() => setIsOpen(false)}
                            style={{
                                position: 'absolute',
                                right: 0,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                display: isOpen ? 'grid' : 'none',
                            }}
                            aria-label="Fechar menu"
                        >
                            <FiX size={18} />
                        </button>
                    </div>
                </div>

                <div className={styles.userCard}>
                    <div className={styles.avatar}>{getInitials(userName)}</div>
                    <div className={styles.userInfo}>
                        <div className={styles.userName}>{userName}</div>
                        {userEmail && <div className={styles.userEmail}>{userEmail}</div>}
                    </div>
                </div>

                <nav className={styles.nav}>
                    <div className={styles.navLabel}>Menu</div>
                    {menuItems.map(({ label, icon: Icon, path }) => {
                        const active = location.pathname === path;
                        return (
                            <button
                                key={path}
                                className={`${styles.navItem} ${active ? styles.navItemActive : ''}`}
                                onClick={() => navigate(path)}
                            >
                                <span className={styles.navIcon}><Icon size={16} /></span>
                                {label}
                            </button>
                        );
                    })}
                </nav>

                {/* Botões de loja — logos reais + links das stores (ver StoreButtons). */}
                <div className={styles.appBadges}>
                    <AppleStoreButton />
                    <GooglePlayButton />
                </div>

                {/* Links de Política e Termos */}
                <div className={styles.policyLinks}>
                    <a
                        href="/politicadeprivacidade"
                        className={styles.policyLink}
                        onClick={(e) => { e.preventDefault(); navigate('/politicadeprivacidade'); }}
                    >
                        <FiShield size={14} /> Política de privacidade
                    </a>
                    <a
                        href="/termosdeuso"
                        className={styles.policyLink}
                        onClick={(e) => { e.preventDefault(); navigate('/termosdeuso'); }}
                    >
                        <FiBookOpen size={14} /> Termos de uso
                    </a>
                </div>

                <div className={styles.sidebarFooter}>
                    <button className={styles.logoutButton} onClick={handleLogout}>
                        <span className={styles.navIcon}><FiLogOut size={18} /></span>
                        Sair
                    </button>
                </div>
            </aside>

            <main className={styles.content}>
                <EmailVerificationBanner
                    user={user}
                    onResent={(alreadyVerified) => {
                        if (alreadyVerified) marcarEmailConfirmadoLocal();
                    }}
                />
                {children}
            </main>
        </div>
    );
}
